import { useRef, useState } from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckoutComponenetList from "../../../components/CustomCheckout/CheckoutComponentList";
import {
  TCustomCheckoutComponentItemSX,
  TCustomCheckoutLayout,
  TModelType,
  TPopupConfig,
} from "../../../core/types/CustomCheckout";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import { useCheckoutBuilder } from "../../../core/context/CheckoutBuilderContext";
import { TDropZoneTarget } from "../../../core/context/CheckoutBuilderContext";

export interface ICustomCheckoutLayoutProps {
  popConfig: TPopupConfig;
  checkoutSettings: TCustomCheckoutComponentItemSX;
  tabIndex: (value: number) => void;
}

/**
 * Componente de zona de drop fixa (topo ou rodapé).
 *
 * IMPORTANTE: Usa altura FIXA (48px) para evitar flickering.
 * Quando a zona muda de tamanho ao ativar, o layout empurra o conteúdo,
 * o mouse "sai" da zona, dispara dragLeave, ela encolhe, o mouse "volta"...
 * gerando um loop infinito de resize.
 *
 * Usa um counter de dragEnter/dragLeave para lidar com eventos de filhos
 * (em vez de confiar em e.target === e.currentTarget, que é frágil).
 */
const DropZone = ({
  zone,
  dropZoneTarget,
  dragOverItem,
  activeZone,
  setActiveZone,
}: {
  zone: TDropZoneTarget;
  dropZoneTarget: React.MutableRefObject<TDropZoneTarget>;
  dragOverItem: React.MutableRefObject<string>;
  activeZone: TDropZoneTarget;
  setActiveZone: (z: TDropZoneTarget) => void;
}) => {
  const isActive = activeZone === zone;
  const label =
    zone === "top"
      ? "Solte aqui para adicionar no topo"
      : "Solte aqui para adicionar no rodapé";
  // Counter para rastrear enters/leaves de elementos filhos
  const enterCount = useRef(0);

  return (
    <Box
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        enterCount.current += 1;
        // Só ativa na primeira entrada real
        if (enterCount.current === 1) {
          dropZoneTarget.current = zone;
          dragOverItem.current = "";
          setActiveZone(zone);
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDragLeave={(e) => {
        e.stopPropagation();
        enterCount.current -= 1;
        // Só desativa quando realmente saiu da zona (não apenas de um filho)
        if (enterCount.current <= 0) {
          enterCount.current = 0;
          if (dropZoneTarget.current === zone) {
            dropZoneTarget.current = "";
          }
          setActiveZone("");
        }
      }}
      onDrop={(_e) => {
        // Reset do counter ao soltar
        enterCount.current = 0;
        setActiveZone("");
      }}
      sx={{
        width: "100%",
        height: 48,
        border: `2px dashed ${isActive ? blue[500] : "transparent"}`,
        borderRadius: "8px",
        backgroundColor: isActive ? blue[50] : "transparent",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        my: 0.5,
      }}
    >
      {/* pointer-events: none impede que filhos disparem dragEnter/dragLeave */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          pointerEvents: "none",
          opacity: isActive ? 1 : 0,
          transition: "opacity 0.15s ease",
        }}
      >
        <AddCircleOutlineIcon sx={{ color: blue[500], fontSize: 18 }} />
        <Typography variant="caption" sx={{ color: blue[700], fontWeight: 500 }}>
          {label}
        </Typography>
      </Stack>
    </Box>
  );
};

const CustomCheckoutLayout = ({
  popConfig,
  checkoutSettings,
  tabIndex,
}: ICustomCheckoutLayoutProps) => {
  const { layout, dragOverItem, dropZoneTarget } = useCheckoutBuilder();
  const [activeZone, setActiveZone] = useState<TDropZoneTarget>("");

  return (
    <>
      <Box
        sx={{
          width: "100%",
          overflowY: "scroll",
          maxHeight: "87vh",
        }}
      >
        <Box
          sx={{
            backgroundColor: checkoutSettings.backgroundColor,
            backgroundImage: `url("${checkoutSettings.backgroundImage}")`,
            backgroundRepeat: checkoutSettings.backgroundImageRepeat ? "repeat" : "no-repeat",
            backgroundPositionX: checkoutSettings.backgroundImageRepeat ? "start" : "center",
            backgroundPositionY: checkoutSettings.backgroundImageRepeat
              ? "start"
              : checkoutSettings.backgroundImageFixedTop
                ? "start"
                : "center",
            backgroundSize: checkoutSettings.backgroundImageExpand ? "cover" : "auto",
            backgroundAttachment: checkoutSettings.backgroundImageFixedTop ? "fixed" : "scroll",
          }}
        >
          <Container maxWidth={layout === TCustomCheckoutLayout.mobile ? "xs" : "md"}>
            {/* ─── Drop Zone Topo ──────────────────────────────────── */}
            <DropZone
              zone="top"
              dropZoneTarget={dropZoneTarget}
              dragOverItem={dragOverItem}
              activeZone={activeZone}
              setActiveZone={setActiveZone}
            />

            <CheckoutComponenetList />

            {/* ─── Drop Zone Rodapé ────────────────────────────────── */}
            <DropZone
              zone="bottom"
              dropZoneTarget={dropZoneTarget}
              dragOverItem={dragOverItem}
              activeZone={activeZone}
              setActiveZone={setActiveZone}
            />
          </Container>

          {popConfig.modelType !== TModelType.none && (
            <Box
              sx={{
                position: "absolute",
                border: "2px dotted grey",
                padding: 2,
                backgroundColor: "white",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginLeft: "auto",
                bottom: 20,
                right: 550,
              }}
            >
              <Typography align="center" color="Black">
                FUNCIONALIDADE
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AutoAwesomeMotionIcon />}
                onClick={() => tabIndex(2)}
                sx={{
                  marginTop: 1,
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                }}
              >
                Exit Popup
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CustomCheckoutLayout;
